import React from 'react';
import { Link } from 'react-router-dom';
import { Page, useOrgName, useShellStatus } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { accountTimeZone, timeZoneAbbreviation } from '../../design/timeZone';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { PDF_SUMMARY } from '../PlaceholderPage';
import { Card, Delta, Kpi, KpiLedger } from '../../components/shared/Card';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { BarChart, HBars, LineChart, useTableToggle } from '../../components/shared/charts';
import { BEFORE_HISTORY, analyticsApi, dayCount, hourLabel, isWeekday, nameList, priorGap, shortDate, useAnalytics, weekdayDate } from '../../services/analyticsApi';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = { Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun' };

const clock = (hhmmss) => {
  const [h, m] = String(hhmmss).split(':').map(Number);
  return `${((h + 11) % 12) + 1}:${m < 10 ? '0' : ''}${m} ${h < 12 ? 'AM' : 'PM'}`;
};

/** "Mon–Fri 8:00 AM–6:00 PM ET" from the weekly hours (open days with the same first interval) in the account zone. */
function hoursText(weekly, zone) {
  const open = DAY_ORDER.map((d) => weekly.find((w) => w.day === d)).filter((w) => w && w.isOpen && w.intervals.length);
  if (!open.length) return null;
  const first = open[0].intervals[0];
  const same = open.every((w) => w.intervals[0].start === first.start && w.intervals[0].end === first.end);
  const days = open.length > 2 && DAY_ORDER.indexOf(open[open.length - 1].day) - DAY_ORDER.indexOf(open[0].day) === open.length - 1 ? `${DAY_SHORT[open[0].day]}–${DAY_SHORT[open[open.length - 1].day]}` : open.map((w) => DAY_SHORT[w.day]).join(', ');
  return same ? `${days} ${clock(first.start)}–${clock(first.end)} ${timeZoneAbbreviation(zone)}` : `${days}, hours vary by day`;
}

function Targets({ data }) {
  const t = data.targets;
  const d = t.default;
  const ignore = t.ignoreShortCallsUnderSeconds;
  const hours = hoursText(t.weeklyHours, t.timeZone || accountTimeZone());
  const overrides = t.queueTargets;
  const items = [
    ['target', 'Answer speed', `${Number(d.answerTargetPercent)}% of queued calls answered within ${d.answerWithinSeconds} seconds; voicemail and missed calls count as not met`],
    ['x', 'Abandonment', `Under ${Number(d.abandonTargetPercent)}% of inbound calls abandoned${ignore ? ` (hang-ups under ${ignore} seconds excluded)` : ''}`],
    ['clock', 'When it counts', hours ? `Business hours, ${hours}${t.holidayCount ? `, excluding ${t.holidayCount} ${t.holidayCount === 1 ? 'holiday' : 'holidays'}` : ''}` : 'Every hour (no business hours are set)'],
    [
      'layers',
      'Where it applies',
      overrides.length
        ? `All queues except ${overrides.map((o) => `${o.queueName} (${Number(o.targets.answerTargetPercent)}% in ${o.targets.answerWithinSeconds} seconds)`).join(', ')}`
        : 'All queues',
    ],
  ];
  return (
    <section className="card">
      <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 }}>
        {items.map(([icon, title, text]) => (
          <div key={title} className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <span style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--accent-soft)', color: 'var(--accent-ink)', display: 'grid', placeItems: 'center', flex: 'none' }}>
              <Icon name={icon} />
            </span>
            <div>
              <div className="small strong">{title}</div>
              <div className="small sub">{text}</div>
            </div>
          </div>
        ))}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Link className="btn btn-sm" to="/admin/sla">
            <Icon name="edit" className="ico-sm" />
            Edit targets
          </Link>
        </div>
      </div>
    </section>
  );
}

function TrendCard({ data }) {
  const table = useTableToggle();
  const target = Number(data.targets.default.answerTargetPercent);
  const days = data.daily.filter((d) => d.total > 0 || isWeekday(d.date)).filter((d) => d.total > 0);
  const values = days.map((d) => Number(d.serviceLevelPercent));
  const low = Math.min(60, ...values.map((v) => Math.floor(v / 10) * 10));
  return (
    <Card className="c-8" title="SLA met, last 30 days" description={`Business days only; dashed line is the ${target}% target`} actions={table.button}>
      <LineChart
        labels={days.map((d) => shortDate(d.date))}
        xName="Day"
        height={250}
        yMin={low}
        yMax={100}
        yStep={10}
        yFmt={(v) => `${v}%`}
        vFmt={(v) => `${Number(v).toFixed(1)}%`}
        area
        series={[{ name: 'SLA met', values, slot: 1 }]}
        target={{ value: target, label: `Target ${target}%` }}
        showTable={table.on}
      />
    </Card>
  );
}

function HourCard({ data }) {
  const table = useTableToggle();
  const target = Number(data.targets.default.answerTargetPercent);
  const byHour = data.byHour.filter((x) => x.total > 0);
  return (
    <Card className="c-6" title="By hour of day" description="Average across business days in this period" actions={table.button}>
      <BarChart
        labels={byHour.map((x) => hourLabel(x.hour))}
        xName="Hour"
        height={230}
        yMax={100}
        yFmt={(v) => `${v}%`}
        vFmt={(v) => `${Number(v).toFixed(1)}%`}
        target={{ value: target, label: `Target ${target}%` }}
        series={[{ name: 'SLA met', values: byHour.map((x) => Number(x.serviceLevelPercent)), slot: 1 }]}
        showTable={table.on}
      />
    </Card>
  );
}

const windowText = (start, end) => {
  const s = Number(start.slice(11, 13));
  const e = Number(end.slice(11, 13));
  const label = (h) => `${((h + 11) % 12) + 1}:00`;
  const half = (h) => (h % 24 < 12 ? 'AM' : 'PM');
  return half(s) === half(e) ? `${label(s)}–${label(e)} ${half(e)}` : `${label(s)} ${half(s)}–${label(e)} ${half(e)}`;
};

export function SlaPage() {
  const org = useOrgName();
  const h = useHierarchyFilter();
  const { data, error, retry } = useAnalytics(() => analyticsApi.sla(h.params), JSON.stringify(h.params));
  const days = dayCount(h.range.from, h.range.to);
  const { status: shell } = useShellStatus();

  let body;
  if (error) body = <ErrorState title="SLA couldn't load" body="Vertolytics couldn't reach the analytics service. Service level figures are unavailable until it responds." error={error} onRetry={retry} />;
  else if (!data) body = <Skeleton height={420} />;
  else if (!data.totals.serviceLevelCalls)
    body = (
      <div className="stack-lg">
        <Targets data={data} />
        <EmptyState icon="target" title="No business-hours calls in this period" body="SLA only counts calls that arrive during business hours. This range falls on weekends or holidays." />
      </div>
    );
  else {
    const t = data.totals;
    const d = data.targets.default;
    const target = Number(d.answerTargetPercent);
    const gap = priorGap({ from: h.range.from, to: h.range.to, historyStartDate: shell?.callHistoryStartDate, hasPrior: t.previousServiceLevelPercent != null });
    const delta = gap ? null : t.serviceLevelPercent - t.previousServiceLevelPercent;
    const overrides = new Set(data.targets.queueTargets.map((q) => q.queueId));
    const queueRows = data.queues.filter((q) => !overrides.has(q.queueId) && q.serviceLevelCalls > 0).sort((a, b) => a.answeredWithinTargetPercent - b.answeredWithinTargetPercent);
    body = (
      <div className="stack-lg">
        <Targets data={data} />
        <KpiLedger>
          <Kpi
            label="SLA met"
            value={`${t.serviceLevelPercent.toFixed(1)}%`}
            meta={
              gap === BEFORE_HISTORY ? (
                BEFORE_HISTORY
              ) : delta == null ? (
                `target ${target}%`
              ) : (
                <>
                  <Delta direction={Math.abs(delta) < 0.05 ? 'flat' : delta > 0 ? 'up-good' : 'down-bad'}>{`${Math.abs(delta).toFixed(1)} pts`}</Delta> vs prior {days} days
                </>
              )
            }
          />
          <Kpi label={`Answered within ${d.answerWithinSeconds} s`} value={fmt.int(t.answeredWithinTarget)} meta={`of ${fmt.int(t.serviceLevelCalls)} queued calls`} />
          <Kpi label="Abandon rate" value={`${t.abandonRate.toFixed(1)}%`} meta={`target under ${Number(d.abandonTargetPercent)}%`} />
          <Kpi label="Days below target" value={`${t.daysBelowTarget.length} of ${t.daysWithCalls}`} meta={t.daysBelowTarget.length ? nameList(t.daysBelowTarget.map(weekdayDate)) : 'every day on target'} />
          <Kpi label="Queues below target" value={`${t.queuesBelowTarget.length} of ${t.queuesWithCalls}`} meta={t.queuesBelowTarget.length ? nameList(t.queuesBelowTarget) : 'every queue on target'} />
        </KpiLedger>
        <div className="grid">
          <TrendCard data={data} />
          <Card className="c-4" title={`By ${data.topLevelName}`} description={`Tick marks the ${target}% target`}>
            <HBars
              max={100}
              format={(v) => `${Number(v).toFixed(1)}%`}
              items={data.byNode
                .filter((n) => n.total > 0)
                .slice()
                .sort((a, b) => b.serviceLevelPercent - a.serviceLevelPercent)
                .map((n) => ({ label: n.name, value: Number(n.serviceLevelPercent), marker: target, color: n.serviceLevelPercent < target ? 'var(--critical-mark)' : 'var(--series-1)' }))}
            />
          </Card>
          <Card className="c-6" title="By queue" description={`Queues with a target of ${target}% in ${d.answerWithinSeconds} seconds`}>
            <HBars
              max={100}
              labelWidth="1.7fr"
              format={(v) => `${Number(v).toFixed(1)}%`}
              items={queueRows.map((q) => ({ label: q.queueName, value: Number(q.answeredWithinTargetPercent), marker: target, color: q.answeredWithinTargetPercent < target ? 'var(--critical-mark)' : 'var(--series-1)' }))}
            />
          </Card>
          <HourCard data={data} />
          <Card className="c-12" flush title="Breach windows" description="Hours when a queue fell below its target, most recent first">
            {data.breachWindows.length ? (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Window</th>
                      <th>Queue</th>
                      <th className="n">SLA met</th>
                      <th className="n">Calls</th>
                      <th className="n">Longest wait</th>
                      <th>What was happening</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.breachWindows.map((b) => (
                      <tr key={`${b.queueId}-${b.start}`}>
                        <td className="nowrap">{weekdayDate(b.start)}</td>
                        <td className="nowrap">{windowText(b.start, b.end)}</td>
                        <td className="cell-title">{b.queueName}</td>
                        <td className="n">
                          <span className="sev sev-immediate">
                            <Icon name="alert" />
                            {Number(b.serviceLevelPercent).toFixed(1)}%
                          </span>
                        </td>
                        <td className="n">{b.calls}</td>
                        <td className="n">{fmt.mmss(b.longestWaitSeconds)}</td>
                        <td className="small sub">{b.topCategoryName ? `${b.topCategoryPercent}% of analyzed calls were ${b.topCategoryName}` : 'No analyzed calls in this window'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="caption" style={{ padding: 16 }}>
                No queue fell below its target for a full hour in this period.
              </div>
            )}
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Page
      title="SLA"
      subtitle={`How often ${org} answered within its targets. Targets and business hours are set in SLA and hours.`}
      exportConfig={{ name: 'SLA', endpoint: '/export/datasets/ServiceLevel', params: { title: 'SLA' }, extraFormats: PDF_SUMMARY('SLA summary for the board packet (.pdf)') }}
    >
      {body}
    </Page>
  );
}
